import { Component, h, Host, Element, State, Prop } from '@stencil/core';
import Glide from '@glidejs/glide';
import { Job, UiService, getConfig } from '@recruiting-solutions/rs-shared';
import { ArvIcons } from 'apps/arv-components/src/global/arv-icons';
import { SearchRequest, SearchService } from '@recruiting-solutions/search-ui-shared';
import { ArvButtonAnimated } from '../arv-button-animated/arv-button-animated';
import { IAnimatedButtonFontParams, asSans16, blackBlueNoBG } from '../arv-button-animated/arv-button-animated-params';

const maxPerView = 3;
@Component({
    tag: 'arv-insights',
    styleUrl: 'arv-insights.scss',
    shadow: true,
})
export class ArvInsights {
    @Element() element: HTMLElement;

    @Prop() jobIds: string = '24580-de_DE,34442-de_DE,369444-de_DE';
    @Prop() heading: string = 'Wir haben zurzeit <b> {count} offene stellen</b> und das sind unsere highlights';
    @Prop() linkText: string = 'Alle Jobs anzeigen';
    @Prop() buttonLink: string = 'https://jobsearch.createyourowncareer.com/ARVATO';

    @State() slideItems;
    @State() jobs: Job[] = [];
    @State() isTablet = false;
    @State() isMobile = false;
    @State() count: number = 0;
    @State() hideArrows: boolean = false;

    _jobIds: string[] = ['24580-de_DE', '34442-de_DE', '369444-de_DE'];
    glideElement: HTMLDivElement;
    sliderRef: HTMLUListElement;
    glide: Glide;
    storageUrl: string;
    peek: number = 0;

    async componentWillLoad() {
        const slotItems = this.element.children;
        this.slideItems = Array.from(slotItems);

        this.storageUrl = getConfig().storageUrl;
        if (this.jobIds) {
            this._jobIds = this.jobIds.split(',');
        }
        this.jobs = await SearchService.getJobs(this._jobIds);

        // First sort, then....
        this.jobs?.sort((a, b) => this.sortFunction(a, b));
        // ...we have to normalize the amount of slides, but only if more than one job, because else we do not show a slider.
        if (this.jobs.length > 1) {
            while (this.jobs.length > 0 && this.jobs.length < maxPerView) {
                this.jobs.push(...this.jobs);
            }
        }

        const request = new SearchRequest('*');
        request.count = true;
        SearchService.search(request).then(result => {
            this.count = result.count;
        });
        UiService.isMobile.subscribe(isMobile => (this.isMobile = isMobile));
        UiService.isTablet.subscribe(isTablet => (this.isTablet = isTablet));
    }

    sortFunction(a: Job, b: Job) {
        return this._jobIds.indexOf(a.jobId) > this._jobIds.indexOf(b.jobId) ? 1 : -1;
    }

    render() {
        let fontTheme: IAnimatedButtonFontParams = asSans16();
        fontTheme.fontSize = '16px';
        return (
            <Host>
                <h2 class="headline" innerHTML={this.heading.replace('{count}', (this.count || 0).toString())} />
                {this.jobs?.length > 1 ? this.renderSlider() : this.renderSingleSlide()}
                <div class="link">{ArvButtonAnimated.asHref(this.linkText, this.buttonLink, '', blackBlueNoBG(), fontTheme, null, false, true)}</div>
            </Host>
        );
    }

    renderSlider() {
        return (
            <div class="carousel">
                <div class="glide" ref={element => (this.glideElement = element)}>
                    <div class="glide__track" data-glide-el="track">
                        <ul class="glide__slides" ref={ref => (this.sliderRef = ref)}>
                            {this.jobs?.map((job: Job) => {
                                return <li class={{ glide__slide: true }}>{this.renderSlide(job)}</li>;
                            })}
                        </ul>
                    </div>
                    <div class="glide__arrows" data-glide-el="controls">
                        <button class={{ glide__arrow: true, 'glide__arrow--left': true, 'hide-arrows': this.hideArrows }} data-glide-dir="<" aria-label="previous">
                            {ArvIcons.getGeneric('arrow_left', 'black', this.isMobile ? 24 : 40, 'fas fa-arrow-left')}
                        </button>
                        <button class={{ glide__arrow: true, 'glide__arrow--right': true, 'hide-arrows': this.hideArrows }} data-glide-dir=">" aria-label="next">
                            {ArvIcons.getGeneric('arrow_right', 'black', this.isMobile ? 24 : 40, 'fas fa-arrow-right')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderSingleSlide() {
        if (this.jobs && this.jobs.length > 0) {
            return <div class="single-slide">{this.renderSlide(this.jobs[0])}</div>;
        }
        return '';
    }

    renderSlide(job: Job) {
        return (
            <div class="slide-container">
                <div class="card">
                    <a class="inner-card" href={job.link} target="_blank">
                        <div class="badges">{job.jobFamily ? <div class="badge">{job.jobFamily}</div> : null}</div>
                        <div class="body">
                            <div class="location">{job.location?.countryCity.split(':')[1]}</div>
                            <a class="title" title={job.title} innerHTML={job.title}></a>
                        </div>
                    </a>
                </div>
            </div>
        );
    }

    componentDidLoad() {
        if (this.jobs?.length > 1) {
            this.glide = new Glide(this.glideElement, {
                type: 'carousel',
                focusAt: 'center',
                perView: Math.min(maxPerView, 3),
                swipeThreshold: 100, // Minimal swipe distance needed to change the slide
                dragThreshold: 100, // Minimal mousedrag distance needed to change the slide
                perTouch: 1,
                animationDuration: 600,
                breakpoints: {
                    1800: {
                        perView: Math.min(maxPerView, 1),
                    },
                },
            });

            this.glide.mount();
            this.glide.on('resize', () => this.onResize());
            this.glide.on('move', () => {
                this.hideArrows = true;
            });
            this.glide.on('move.after', () => {
                this.hideArrows = false;
            });
            this.onResize();
        }
    }

    onResize() {
        const peekMinimize = 50;
        const threshold = 680;

        let peek = 0;
        if (window.innerWidth < 1800) {
            let slideWidth = parseInt(this.sliderRef.style.width, 0) / this.sliderRef.childElementCount;
            //below this threshold the slides stay at their position and dont move inwards any further
            if (slideWidth <= threshold) {
                peek = (slideWidth - threshold / 2) / 2;
            } else {
                peek = slideWidth / 2 / 2;
                peek -= peekMinimize;
            }
            peek = Math.max(peek, 0);
        }
        if (peek !== this.peek) {
            this.peek = peek;
            this.glide.update({ peek: peek });
        }
    }
}
